// The inline Google tag bootstrap. One script, strictly ordered, so Consent
// Mode defaults are in the dataLayer BEFORE any Google script can read it:
//   1. dataLayer + gtag shim
//   2. consent default — everything denied
//   3. consent update from the diner's saved cookie, if any
//   4. the loader (GTM container or GA4 gtag.js)
//   5. the ready flag + event — client code queues events until then, so no
//      event can land in the dataLayer ahead of the consent defaults
import type { GoogleTag } from "@workspace/model"

import {
  CONSENT_COOKIE,
  CONSENT_VALUE_PATTERN,
  DEFAULT_CONSENT_MODE,
  toConsentMode,
} from "./consent.ts"
import { TAG_READY_EVENT, TAG_READY_FLAG } from "./emit.ts"

export const GOOGLE_TAG_ORIGIN = "https://www.googletagmanager.com"

/** How long Google tags wait for a `consent update` before firing denied pings. */
const WAIT_FOR_UPDATE_MS = 500

export function loaderUrl(tag: GoogleTag): string {
  const path = tag.kind === "gtm" ? "gtm.js" : "gtag/js"
  return `${GOOGLE_TAG_ORIGIN}/${path}?id=${encodeURIComponent(tag.id)}`
}

/**
 * Builds the bootstrap source. `tag.id` is already pattern-validated by
 * `resolveGoogleTag`; every interpolated value is ALSO `JSON.stringify`-ed so
 * nothing can break out of its string literal even if that guard regressed.
 */
export function buildBootstrapScript(tag: GoogleTag): string {
  const cookieRe = `(?:^|; )${CONSENT_COOKIE}=${CONSENT_VALUE_PATTERN}(?:;|$)`
  const defaults = {
    ...DEFAULT_CONSENT_MODE,
    wait_for_update: WAIT_FOR_UPDATE_MS,
  }
  // Consent Mode params per cookie digits `<analytics><ads>`, from the same
  // mapping the banner uses — never restated by hand in the inline script.
  const modes: Record<string, unknown> = {}
  for (const analytics of [false, true]) {
    for (const ads of [false, true]) {
      modes[`${analytics ? 1 : 0}${ads ? 1 : 0}`] = toConsentMode({
        analytics,
        ads,
      })
    }
  }
  const loader =
    tag.kind === "gtm"
      ? `w.dataLayer.push({"gtm.start":new Date().getTime(),event:"gtm.js"});`
      : `gtag("js",new Date());gtag("config",${JSON.stringify(tag.id)});`

  return [
    "(function(w,d){",
    "w.dataLayer=w.dataLayer||[];",
    "function gtag(){w.dataLayer.push(arguments)}",
    "w.gtag=w.gtag||gtag;",
    `gtag("consent","default",${JSON.stringify(defaults)});`,
    'gtag("set","ads_data_redaction",true);',
    `var m=d.cookie.match(new RegExp(${JSON.stringify(cookieRe)}));`,
    `if(m){gtag("consent","update",${JSON.stringify(modes)}[m[1]+m[2]]);}`,
    loader,
    'var s=d.createElement("script");s.async=true;',
    `s.src=${JSON.stringify(loaderUrl(tag))};`,
    "d.head.appendChild(s);",
    `w[${JSON.stringify(TAG_READY_FLAG)}]=true;`,
    `if(w.dispatchEvent)w.dispatchEvent(new Event(${JSON.stringify(TAG_READY_EVENT)}));`,
    "})(window,document);",
  ].join("")
}
